import { createContext, Dispatch, SetStateAction, useState } from 'react';
import type { ComponentType } from 'react';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { requireAuth } from 'Shared/require-auth';
import LoadingIndicator from 'Shared/loading_indicator';
import ApiRequest from 'Shared/api_2_0';

const noop = () => {};

const defaultContext = {
   isCompleted: false,
   complete: noop as (_guideId: number) => void,
   removeComplete: noop as (_guideId: number) => void,
   completedCount: 0,
};

const GuideCompletionContext = createContext(defaultContext);

function doCompletionGuide(
   guideId: number,
   wantsToComplete: boolean,
   setIsCompleted: Dispatch<SetStateAction<boolean>>,
   setCompletedCount: Dispatch<SetStateAction<number>>,
   completedCount: number
) {
   requireAuth({
      from: '',
      message: _js('Log in to record that you completed a repair.'),
      onAuthorize: LoadingIndicator.withEventHandler(
         () => {
            const method = wantsToComplete ? 'PUT' : 'DELETE';
            const url = `guides/${guideId}/completed`;

            const request = new ApiRequest(url, { method: method });
            return request.send({ langid: App.langid }).then(() => {
               trackAnalyticsCustomEvent({
                  eventCategory: 'Guide Author Reward',
                  eventAction: wantsToComplete
                     ? 'Guide Author Reward - Form "Give the author +30 points" - Submit'
                     : 'Guide Author Reward - Cancel points',
               });
               setIsCompleted(wantsToComplete);
               setCompletedCount(wantsToComplete ? completedCount + 1 : completedCount - 1);
            });
         },
         {
            pendingMessage: wantsToComplete ? _js('Adding Completion') : _js('Removing Completion'),
            successMessage: wantsToComplete ? _js('Added Completion') : _js('Removed Completion'),
            failureMessage: _js('Failure'),
         }
      ),
   });
}

function withGuideCompletion<T>(Component: ComponentType<T>) {
   return function GuideCompletionProvider(
      props: { isCompleted?: boolean; completedCount: number } & T
   ) {
      const [isCompleted, setIsCompleted] = useState(
         props.isCompleted || defaultContext.isCompleted
      );

      const [completedCount, setCompletedCount] = useState(
         props.completedCount || defaultContext.completedCount
      );

      const guideCompletionContext = {
         complete: (guideid: number) => {
            doCompletionGuide(guideid, true, setIsCompleted, setCompletedCount, completedCount);
         },
         removeComplete: (guideid: number) => {
            doCompletionGuide(guideid, false, setIsCompleted, setCompletedCount, completedCount);
         },
         isCompleted,
         completedCount,
      };

      return (
         <GuideCompletionContext.Provider value={guideCompletionContext}>
            <Component {...props} />
         </GuideCompletionContext.Provider>
      );
   };
}

export { GuideCompletionContext, withGuideCompletion };
